import React, { useState } from 'react';
import { ScriptGenerationEditorProps, ScriptScene } from '../types';
import Typography from '../../../basic/Typography';
import { newsEditorApi } from '../services/newsEditorApi';
import styles from '../NewsEditor.module.css';

const ScriptGenerationEditor: React.FC<ScriptGenerationEditorProps> = ({
  jobId,
  script,
  onUpdate,
  onRerun
}) => {
  const [isAddingScene, setIsAddingScene] = useState(false);
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [newScene, setNewScene] = useState<Omit<ScriptScene, 'id'>>({
    name: '',
    type: 'headline',
    duration_sec: 30,
    dialogue: '',
    transition: '',
    mood: 'neutral'
  });

  // Check if we have real data or just empty fallback
  const hasRealData = script.scenes.length > 0 || script.metadata.last_updated !== new Date().toISOString();

  // Debug logging
  console.log('ScriptGenerationEditor received script:', script);
  console.log('Script scenes count:', script.scenes.length);
  console.log('Has real data:', hasRealData);
  console.log('Script metadata:', script.metadata);

  const handleAddScene = async () => {
    try {
      // For now, create a scene with a generated ID
      const addedScene: ScriptScene = {
        id: `scene-${Date.now()}`,
        ...newScene
      };

      const updatedScenes = [...script.scenes, addedScene];
      const updatedScript = {
        ...script,
        scenes: updatedScenes,
        metadata: {
          ...script.metadata,
          total_duration: updatedScenes.reduce((sum, scene) => sum + scene.duration_sec, 0),
          scene_count: updatedScenes.length,
          last_updated: new Date().toISOString()
        }
      };

      onUpdate(updatedScript);
      setHasUnsavedChanges(true);

      // Reset form
      setNewScene({
        name: '',
        type: 'headline',
        duration_sec: 30,
        dialogue: '',
        transition: '',
        mood: 'neutral'
      });
      setIsAddingScene(false);
    } catch (error) {
      console.error('Error adding scene:', error);
      alert('Failed to add script scene');
    }
  };

  const handleDeleteScene = async (sceneId: string) => {
    if (window.confirm('Are you sure you want to delete this scene?')) {
      try {
        const updatedScenes = script.scenes.filter(scene => scene.id !== sceneId);
        const updatedScript = {
          ...script,
          scenes: updatedScenes,
          metadata: {
            ...script.metadata,
            total_duration: updatedScenes.reduce((sum, scene) => sum + scene.duration_sec, 0),
            scene_count: updatedScenes.length,
            last_updated: new Date().toISOString()
          }
        };
        onUpdate(updatedScript);
        setHasUnsavedChanges(true);
      } catch (error) {
        console.error('Error deleting scene:', error);
        alert('Failed to delete script scene');
      }
    }
  };

  const handleEditScene = (scene: ScriptScene) => {
    setEditingSceneId(scene.id);
    setNewScene({
      name: scene.name,
      type: scene.type,
      duration_sec: scene.duration_sec,
      dialogue: scene.dialogue,
      transition: scene.transition,
      mood: scene.mood
    });
    setIsAddingScene(true);
  };

  const handleUpdateScene = async () => {
    if (!editingSceneId) return;

    try {
      const updatedScenes = script.scenes.map(scene =>
        scene.id === editingSceneId ? { ...scene, ...newScene } : scene
      );

      const updatedScript = {
        ...script,
        scenes: updatedScenes,
        metadata: {
          ...script.metadata,
          total_duration: updatedScenes.reduce((sum, scene) => sum + scene.duration_sec, 0),
          scene_count: updatedScenes.length,
          last_updated: new Date().toISOString()
        }
      };

      onUpdate(updatedScript);
      setHasUnsavedChanges(true);
      setEditingSceneId(null);
      setIsAddingScene(false);

      // Reset form
      setNewScene({
        name: '',
        type: 'headline',
        duration_sec: 30,
        dialogue: '',
        transition: '',
        mood: 'neutral'
      });
    } catch (error) {
      console.error('Error updating scene:', error);
      alert('Failed to update script scene');
    }
  };

  const handleSaveToBackend = async () => {
    try {
      setIsSaving(true);
      setSaveStatus('Saving changes...');

      // Save the current script to the backend
      await newsEditorApi.saveScriptGeneration(jobId, script);

      setSaveStatus('Changes saved successfully!');
      setHasUnsavedChanges(false);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveStatus('');
      }, 3000);

    } catch (error) {
      console.error('Error saving script:', error);
      setSaveStatus(`Error: ${error instanceof Error ? error.message : 'Failed to save'}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setSaveStatus('');
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRerunScriptGeneration = async () => {
    if (window.confirm('Are you sure you want to rerun script generation? This will regenerate the script from the news events.')) {
      try {
        setIsRerunning(true);
        await onRerun();
        // Note: onRerun will trigger a refresh of the script data
      } catch (error) {
        console.error('Error rerunning script generation:', error);
        alert('Failed to rerun script generation');
      } finally {
        setIsRerunning(false);
      }
    }
  };

  return (
    <div className={styles['script-generation-editor']}>
      <div className={styles['editor-header']}>
        <Typography variant="h4">Script Scenes ({script.scenes.length})</Typography>
        <div className={styles['editor-actions']}>
          <button
            className={styles['add-scene-button']}
            onClick={() => setIsAddingScene(!isAddingScene)}
          >
            {isAddingScene ? 'Cancel' : 'Add Scene'}
          </button>
          <button
            className={styles['save-script-button']}
            onClick={handleSaveToBackend}
            disabled={isSaving || !hasUnsavedChanges}
            title={hasUnsavedChanges ? 'Save changes to backend' : 'No changes to save'}
          >
            {isSaving ? '💾 Saving...' : '💾 Save Changes'}
          </button>
          <button
            className={styles['rerun-button']}
            onClick={handleRerunScriptGeneration}
            disabled={isRerunning}
            title="Rerun script generation to regenerate script from news events"
          >
            {isRerunning ? 'Rerunning...' : 'Rerun Script Generation'}
          </button>
        </div>
      </div>

      {/* Save Status Display */}
      {saveStatus && (
        <div className={`${styles['save-status']} ${saveStatus.includes('Error') ? styles['error'] : styles['success']}`}>
          <Typography variant="body2" className={styles['save-status-text']}>
            {saveStatus}
          </Typography>
        </div>
      )}

      {/* Add/Edit Scene Form */}
      {isAddingScene && (
        <div className={styles['add-scene-form']}>
          <Typography variant="h5">
            {editingSceneId ? 'Edit Scene' : 'Add New Scene'}
          </Typography>
          <div className={styles['form-row']}>
            <input
              type="text"
              placeholder="Scene Name"
              value={newScene.name}
              onChange={(e) => setNewScene({ ...newScene, name: e.target.value })}
              className={styles['form-input']}
            />
            <select
              value={newScene.type}
              onChange={(e) => setNewScene({ ...newScene, type: e.target.value as any })}
              className={styles['form-select']}
            >
              <option value="headline">Headline</option>
              <option value="deep_dive">Deep Dive</option>
              <option value="explainer">Explainer</option>
              <option value="tease">Tease</option>
              <option value="outro">Outro</option>
              <option value="roundup">Roundup</option>
            </select>
          </div>
          <div className={styles['form-row']}>
            <input
              type="number"
              placeholder="Duration (seconds)"
              min="1"
              value={newScene.duration_sec}
              onChange={(e) => setNewScene({ ...newScene, duration_sec: parseInt(e.target.value) })}
              className={styles['form-input']}
            />
            <select
              value={newScene.mood}
              onChange={(e) => setNewScene({ ...newScene, mood: e.target.value as any })}
              className={styles['form-select']}
            >
              <option value="neutral">Neutral</option>
              <option value="urgent">Urgent</option>
              <option value="emotional">Emotional</option>
              <option value="uplifting">Uplifting</option>
            </select>
          </div>
          <div className={styles['form-row']}>
            <textarea
              placeholder="Dialogue/Content"
              value={newScene.dialogue}
              onChange={(e) => setNewScene({ ...newScene, dialogue: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>
          <div className={styles['form-row']}>
            <input
              type="text"
              placeholder="Transition"
              value={newScene.transition}
              onChange={(e) => setNewScene({ ...newScene, transition: e.target.value })}
              className={styles['form-input']}
            />
          </div>
          <div className={styles['form-actions']}>
            <button
              className={styles['save-scene-button']}
              onClick={editingSceneId ? handleUpdateScene : handleAddScene}
              disabled={!newScene.name || !newScene.dialogue}
            >
              {editingSceneId ? 'Update Scene' : 'Add Scene'}
            </button>
          </div>
        </div>
      )}

            <div className={styles['scenes-list']}>
        {!hasRealData ? (
          <div className={styles['no-data-message']}>
            <Typography variant="body1" color="secondary">
              No script generation data available. This may be because:
            </Typography>
            <ul>
              <li>The script generation stage hasn't run yet</li>
              <li>There was an error loading the data</li>
              <li>The job is still in progress</li>
            </ul>
            <Typography variant="body2" color="secondary">
              Try clicking the "Rerun" button above to regenerate the script, or check the job status.
            </Typography>
          </div>
        ) : script.scenes.length === 0 ? (
          <div className="no-scenes-message">
            <Typography variant="body1" color="secondary">
              No scenes have been generated yet. Click "Add Scene" above to create your first scene.
            </Typography>
          </div>
        ) : (
          script.scenes.map((scene) => (
            <div key={scene.id} className={styles['script-scene-item']}>
              <div className={styles['scene-header']}>
                <Typography variant="h5">{scene.name}</Typography>
                <span className={styles['scene-type']}>{scene.type}</span>
                <span className={styles['scene-duration']}>{scene.duration_sec}s</span>
              </div>
              <div className={styles['scene-content']}>
                <Typography variant="body1" className={styles['scene-dialogue']}>{scene.dialogue}</Typography>
                <Typography variant="body2" color="secondary" className={styles['scene-transition']}>Transition: {scene.transition}</Typography>
                <span className={styles['scene-mood']}>Mood: {scene.mood}</span>
                <div className={styles['scene-actions']}>
                  <button
                    className={styles['edit-scene-button']}
                    onClick={() => handleEditScene(scene)}
                    title="Edit this scene"
                  >
                    ✏️
                  </button>
                  <button
                    className={styles['delete-scene-button']}
                    onClick={() => handleDeleteScene(scene.id)}
                    title="Delete this scene"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles['editor-footer']}>
        <Typography variant="caption" color="secondary">Total Duration: {script.metadata.total_duration} seconds</Typography>
        <Typography variant="caption" color="secondary">Scene Count: {script.metadata.scene_count}</Typography>
        <Typography variant="caption" color="secondary">Last Updated: {new Date(script.metadata.last_updated).toLocaleString()}</Typography>
      </div>
    </div>
  );
};

export default ScriptGenerationEditor;
